/**
 * CÓMO SE VE CADA PRODUCTO, EN UN SOLO SITIO.
 * =============================================================================
 *
 * Un producto de GTR se representa en pantalla con tres cosas: un COLOR, un
 * ICONO y una ETIQUETA corta. Hasta hoy esas tres decisiones estaban repetidas
 * en una docena de componentes, cada uno con su propio `switch`, y por eso el
 * mismo paquete salía de un color en la tienda y de otro en «Mi Equipo», o con
 * el icono viejo en una pantalla y el nuevo en otra.
 *
 * Aquí están las tres juntas y una sola vez. Añadir un producto —o cambiarle el
 * icono— es tocar ESTE fichero y nada más.
 *
 * DÓNDE VIVE CADA PIEZA
 * -----------------------------------------------------------------------------
 *   · el COLOR  ... en `assets/styles/gtr-tokens.scss` (`$pkg-colors`), porque
 *     lo consume el CSS. Desde aquí se referencia por su variable CSS
 *     (`var(--pkg-gtr-fly)`) y por las clases utilitarias `.pkg-bg-*`,
 *     `.pkg-text-*` y `.pkg-border-*` que ese mismo mapa genera.
 *   · el ICONO  ... en `PackageIcon.vue`, que es el ÚNICO sitio con los SVG.
 *   · la ETIQUETA y el orden ... aquí abajo.
 *
 * POR QUÉ EL ICONO NO ES UNA CLASE DE FONTAWESOME
 * -----------------------------------------------------------------------------
 * Los tres iconos de paquete son dibujos propios (brújula, cohete, diamante):
 * no existen en FontAwesome con esa forma, y los que se usaban antes
 * (`fa-satellite-dish`, `fa-user-graduate`, `fa-fire`) no significaban nada del
 * producto — eran lo que había a mano cuando se escribió cada pantalla. Al ir
 * en línea tampoco dependen de que la fuente de iconos cargue.
 *
 * EL VOCABULARIO
 * -----------------------------------------------------------------------------
 * Las llaves son el vocabulario VIVO de nivel (`gtr-go`/`gtr-fly`/`gtr-win`),
 * más `license` para la licencia de distribuidor, que NO es un escalón de la
 * escalera sino un producto transversal. Los ids retirados (`gtr-lite`,
 * `trading-academy`, `gtr-vip`, `gtr-basic`) NO aparecen: los traduce
 * `normalizeStoredPackageLabel` antes de llegar aquí, que es la misma puerta
 * por la que pasa todo lo demás.
 */
import {
  ALL_LICENSE_IDS,
  normalizeStoredPackageLabel,
  PKG_FLY,
  PKG_GO,
  PKG_WIN,
  SUB_ACADEMY_LEGACY,
} from "functions/src/Subscriptions/packageIds";

/** La licencia de distribuidor. No es un nivel, así que no vive en `packageIds`. */
export const PRODUCT_LICENSE = "license";

/** Lo que no se ha podido identificar. Se pinta en gris y con una caja. */
export const PRODUCT_UNKNOWN = "unknown";

export type ProductVisualId =
  | typeof PKG_GO
  | typeof PKG_FLY
  | typeof PKG_WIN
  | typeof PRODUCT_LICENSE
  | typeof PRODUCT_UNKNOWN;

export interface ProductVisual {
  /** Etiqueta CORTA, para badges y pastillas donde no cabe el nombre entero. */
  shortLabel: string;
  /** Nombre comercial completo. El del catálogo manda cuando esté cargado. */
  label: string;
  /** El color, como variable CSS — la declara `gtr-tokens.scss`. */
  colorVar: string;
  /** Clase utilitaria de FONDO (la genera el mismo mapa, en `custom-bootstrap.scss`). */
  bgClass: string;
  /** Clase utilitaria de TEXTO. */
  textClass: string;
}

export const PRODUCT_VISUALS: Record<ProductVisualId, ProductVisual> = {
  [PKG_GO]: {
    shortLabel: "Go",
    label: "GTR Go",
    colorVar: "var(--pkg-gtr-go)",
    bgClass: "pkg-bg-gtr-go",
    textClass: "pkg-text-gtr-go",
  },
  [PKG_FLY]: {
    shortLabel: "Fly",
    label: "GTR Fly",
    colorVar: "var(--pkg-gtr-fly)",
    bgClass: "pkg-bg-gtr-fly",
    textClass: "pkg-text-gtr-fly",
  },
  [PKG_WIN]: {
    shortLabel: "Win",
    label: "GTR Win",
    colorVar: "var(--pkg-gtr-win)",
    bgClass: "pkg-bg-gtr-win",
    textClass: "pkg-text-gtr-win",
  },
  [PRODUCT_LICENSE]: {
    shortLabel: "Licencia",
    label: "Licencia de Distribuidor",
    colorVar: "var(--pkg-license)",
    bgClass: "pkg-bg-license",
    textClass: "pkg-text-license",
  },
  [PRODUCT_UNKNOWN]: {
    shortLabel: "—",
    label: "Sin paquete",
    colorVar: "var(--gtr-mid)",
    bgClass: "pkg-bg-unknown",
    textClass: "pkg-text-unknown",
  },
};

/**
 * De CUALQUIER id de producto a la llave con la que se pinta.
 *
 * Acepta los ids retirados y los vivos, la licencia en sus tres formas
 * (`distributor-license`, `lite-distributor-license`, `gtr-distributor-license`)
 * y `none`/`undefined`. Nunca lanza: una pantalla de listado no se puede caer
 * porque un socio viejo tenga un id que ya no existe.
 */
export function productVisualId(
  product: string | undefined | null
): ProductVisualId {
  if (!product) return PRODUCT_UNKNOWN;
  const raw = product.trim();
  // LA PROPIA LLAVE TAMBIÉN ENTRA, y no es redundante: media pantalla no tiene
  // a mano el doc-id de la licencia —sólo sabe «esto es la licencia»— y le pasa
  // la constante `PRODUCT_LICENSE`. Sin esta línea, `"license"` no acaba en
  // `distributor-license` ni lo conoce el normalizador de niveles, así que caía
  // en «desconocido» y la tarjeta de la licencia pintaba la caja gris. Lo mismo
  // vale para los tres niveles: si ya te llega la llave, es la llave.
  if (raw === PRODUCT_LICENSE) return PRODUCT_LICENSE;
  if (raw === PRODUCT_UNKNOWN) return PRODUCT_UNKNOWN;
  // LAS LICENCIAS, POR LISTA EXPLÍCITA Y NO POR EL SUFIJO DEL NOMBRE.
  //
  // Aquí había un `raw.endsWith("distributor-license")`. Acertaba —hoy las tres
  // licencias se llaman así: la de siempre, la del Lite y la GLOBAL— pero
  // acertaba por casualidad de nomenclatura, no porque nadie lo hubiera
  // decidido: bastaba con que alguien acuñara mañana un doc-id acabado en esas
  // dos palabras y que NO fuera una licencia de distribuidor para que esta
  // pantalla le pusiera su icono y su violeta sin preguntar a nadie.
  //
  // El argumento que sostenía el sufijo era que «una lista escrita a mano se
  // habría quedado corta», y era cierto cuando la lista había que escribirla
  // aquí. Ya no hay que escribirla: `ALL_LICENSE_IDS` vive en `packageIds.ts`
  // —la misma fuente única que usa el backend— y la global se añadió allí en su
  // día, así que esta pantalla se habría enterado sola. Una lista que se importa
  // no se queda corta; se queda corta la que se copia.
  //
  // Y si algún día apareciera una cuarta licencia sin darla de alta en
  // `packageIds.ts`, caer en «desconocido» (la caja gris) es la respuesta
  // correcta y no una regresión: ese mismo id tampoco pasaría por
  // `isAcademyLicenseId`, o sea que el backend ya lo estaría ignorando. El gris
  // hace VISIBLE que falta darlo de alta, mientras que el sufijo lo pintaba
  // bonito y dejaba el agujero callado. Es el criterio de «falla cerrado» que ya
  // aplica el resto del fichero.
  if (ALL_LICENSE_IDS.includes(raw)) return PRODUCT_LICENSE;
  // EL ÚNICO DOC-ID DE PAQUETE QUE HAY QUE TRADUCIR A MANO, y merece explicación
  // porque parece que se salta la regla del repo.
  //
  // Nivel y doc-id son dos vocabularios distintos, y `normalizePackageLabel` se
  // planta a propósito cuando le llega un doc-id donde iba un nivel: ahí el
  // error importa, porque decide accesos. Aquí no se decide nada — se elige un
  // dibujo y un color—, así que plantarse o devolver gris sería castigar al
  // usuario por un descuido del código.
  //
  // Y sólo falta ÉSTE. `gtr-lite`, `gtr-vip`, `gtr-go`, `gtr-fly` y `gtr-win`
  // son doc-id Y etiqueta a la vez, así que el normalizador ya los resuelve; la
  // academia vieja es la única que se llama distinto en cada vocabulario
  // (`trading-academy` el documento, `gtr-basic` la etiqueta retirada).
  if (raw === SUB_ACADEMY_LEGACY) return PKG_FLY;
  const level = normalizeStoredPackageLabel(raw);
  if (level === PKG_GO || level === PKG_FLY || level === PKG_WIN) {
    return level;
  }
  return PRODUCT_UNKNOWN;
}

/** Atajo: el bloque visual completo de un producto. */
export function productVisual(
  product: string | undefined | null
): ProductVisual {
  return PRODUCT_VISUALS[productVisualId(product)];
}

/** Los tres paquetes, en el orden de la escalera. Para filtros y leyendas. */
export const PACKAGE_VISUAL_ORDER: ProductVisualId[] = [
  PKG_GO,
  PKG_FLY,
  PKG_WIN,
];
